import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Alert, Snackbar, type AlertColor } from '@mui/material';

/**
 * Application-wide transient feedback.
 *
 * Deliberately minimal: one visible toast at a time, replaced by the next.
 * A queue was considered and rejected — a burst of mutation responses would
 * make the user wait through a backlog of stale messages, and the newest is
 * almost always the one that matters.
 *
 * Toasts are for CONFIRMATION, never for information the user must act on.
 * Validation errors belong on the field; unresolved errors belong in a
 * persistent alert. A message that disappears after four seconds is not a
 * place to put something someone needs to read twice.
 *
 * Nothing sensitive may be passed here — no secrets, tokens, temporary
 * passwords or raw response bodies. Callers pass a short human sentence.
 */
export type ToastSeverity = AlertColor;

interface Toast {
  /** Monotonic id so an identical message still re-announces. */
  key: number;
  message: string;
  severity: ToastSeverity;
}

interface ToastApi {
  showSuccess: (message: string) => void;
  showInfo: (message: string) => void;
  showWarning: (message: string) => void;
  showError: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const AUTO_HIDE_MS: Record<ToastSeverity, number> = {
  success: 4000,
  info: 4000,
  warning: 6000,
  // Errors stay longer: they are more likely to be read, and more costly to miss.
  error: 8000,
};

/** Ignore an identical message repeated inside this window. */
const DUPLICATE_WINDOW_MS = 500;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<Toast | null>(null);
  const counter = useRef(0);
  const last = useRef<{ message: string; at: number } | null>(null);

  const show = useCallback((message: string, severity: ToastSeverity) => {
    const now = Date.now();
    // Several mutations settling together must not produce a storm of the same
    // sentence. Distinct messages always show; identical ones coalesce.
    if (
      last.current &&
      last.current.message === message &&
      now - last.current.at < DUPLICATE_WINDOW_MS
    ) {
      return;
    }
    last.current = { message, at: now };
    counter.current += 1;
    setToast({ key: counter.current, message, severity });
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      showSuccess: (m) => show(m, 'success'),
      showInfo: (m) => show(m, 'info'),
      showWarning: (m) => show(m, 'warning'),
      showError: (m) => show(m, 'error'),
    }),
    [show],
  );

  const close = useCallback((_?: unknown, reason?: string) => {
    // Clicking away should not dismiss — the message may still be being read.
    if (reason === 'clickaway') return;
    setToast(null);
  }, []);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <Snackbar
        key={toast?.key}
        open={toast !== null}
        autoHideDuration={toast ? AUTO_HIDE_MS[toast.severity] : null}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {/* MUI Snackbar needs a single element child; Alert carries the role. */}
        <Alert
          onClose={() => setToast(null)}
          severity={toast?.severity ?? 'info'}
          variant="filled"
          sx={{ width: '100%' }}
          // Errors interrupt; everything else announces politely.
          role={toast?.severity === 'error' ? 'alert' : 'status'}
          aria-live={toast?.severity === 'error' ? 'assertive' : 'polite'}
        >
          {toast?.message ?? ''}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
